import { useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { Button, Notice, PageHead, Spinner, ToastProvider } from 'iboon-ui';

import { Topbar } from './Topbar';
import { api, ApiError, type User } from './api';
import { Assets } from './pages/Assets';
import { Activity } from './pages/Activity';
import { Catalog } from './pages/Catalog';
import { Onboarding } from './pages/Onboarding';
import { Repairs } from './pages/Repairs';
import { Reports } from './pages/Reports';
import { Devices } from './pages/Devices';
import { DeviceDetail } from './pages/DeviceDetail';
import { AssetDetail } from './pages/AssetDetail';
import { Stock } from './pages/Stock';
import { StockDetail } from './pages/StockDetail';
import { People } from './pages/People';
import './shell.css';

/**
 * The console.
 *
 * MeshCentral's shape -- a list of things down the side, the thing you picked
 * in the middle -- because that shape is right for a register and familiar to
 * anybody who has used one. What is absent is the half of MeshCentral this
 * estate does not need: no remote desktop, no terminal, no file transfer.
 * Those are how you *operate* a machine; this is about knowing you own it.
 */
const SECTIONS = [
  { path: '/assets', label: 'Assets', blurb: 'Everything tracked one by one: laptops, monitors, docks.' },
  { path: '/stock', label: 'Stock', blurb: 'Things counted rather than numbered: cables, adapters, spares.' },
  { path: '/devices', label: 'Devices', blurb: 'Machines running iBoon Agent, and who each one belongs to.' },
  { path: '/people', label: 'People', blurb: 'Who holds what, and what comes back when they leave.' },
  { path: '/repairs', label: 'Repairs', blurb: 'What is away being fixed, and what it has cost.' },
  { path: '/onboarding', label: 'Joining & leaving', blurb: 'What a new starter gets, and what comes back when they go.' },
  { path: '/reports', label: 'Labels & reports', blurb: 'Labels to stick on, a walk round to check, and files to send.' },
  { path: '/catalog', label: 'Catalog', blurb: 'The kinds of thing there are, and the places they live.' },
  { path: '/activity', label: 'Activity', blurb: 'Every change anybody has made, most recent first.' },
];

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [problem, setProblem] = useState<{ message: string; status: number } | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    api
      .get<{ user: User }>('/api/auth/me')
      .then(({ user: who }) => setUser(who))
      .catch((error: unknown) => {
        if (error instanceof ApiError) setProblem({ message: error.message, status: error.status });
      })
      .finally(() => setChecked(true));
  }, []);

  if (!checked) return <Spinner />;

  // Not signed in. Sent straight to identity rather than shown a sign-in form
  // of our own -- there is no password to type here and never will be.
  if (!user && problem?.status === 401) {
    window.location.href = '/api/auth/login';
    return <Spinner />;
  }

  /*
   * Signed in, and not an administrator.
   *
   * Said in full, with somewhere to go. "Forbidden" on a blank page leaves
   * somebody wondering whether they are looking at a bug; the useful answer is
   * that their own equipment is listed in the app they already use.
   */
  if (!user && problem?.status === 403) {
    return (
      <div className="shell shell--bare">
        <Notice tone="danger" title="This console is for administrators">
          {problem.message}
        </Notice>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="shell shell--bare">
        <Notice tone="danger" title="Could not check who you are">
          {problem?.message ?? 'The server did not answer.'}
        </Notice>
      </div>
    );
  }

  return (
    <ToastProvider>
      <div className="shell">
        <aside className="side">
          <div className="side__brand">
            MeshCentral <span>by iBoon</span>
          </div>

          <nav className="side__nav">
            {SECTIONS.map((section) => (
              <NavLink key={section.path} to={section.path} className="side__link">
                {section.label}
              </NavLink>
            ))}
          </nav>

          <div className="side__foot">
            Everyone sees their own equipment in Keka. This is the register behind it.
          </div>
        </aside>

        <div className="main">
          <header className="topbar">
            <Topbar signedIn />
          </header>

          <main className="content">
            <Routes>
              <Route path="/" element={<Navigate to="/assets" replace />} />
              <Route path="/assets" element={<Section path="/assets"><Assets /></Section>} />
              <Route path="/assets/:id" element={<AssetDetail />} />
              <Route path="/stock" element={<Section path="/stock"><Stock /></Section>} />
              <Route path="/stock/:id" element={<StockDetail />} />
              <Route path="/devices" element={<Section path="/devices"><Devices /></Section>} />
              <Route path="/devices/:id" element={<DeviceDetail />} />
              <Route path="/people" element={<Section path="/people"><People /></Section>} />
              <Route path="/repairs" element={<Section path="/repairs"><Repairs /></Section>} />
              <Route path="/onboarding" element={<Section path="/onboarding"><Onboarding /></Section>} />
              <Route path="/reports" element={<Section path="/reports"><Reports /></Section>} />
              <Route path="/catalog" element={<Section path="/catalog"><Catalog /></Section>} />
              <Route path="/activity" element={<Section path="/activity"><Activity /></Section>} />
              <Route path="*" element={<Notice tone="danger" title="No such page">Nothing lives at that address.</Notice>} />
            </Routes>
          </main>
        </div>
      </div>
    </ToastProvider>
  );
}

/**
 * The heading for a section, looked up by path.
 *
 * This took its section by array index, which is the sort of thing that is
 * fine at four entries and silently mislabels every page the moment one is
 * inserted in the middle -- which is exactly what adding Repairs did.
 */
function Section({ path, children }: { path: string; children: React.ReactNode }) {
  const section = SECTIONS.find((candidate) => candidate.path === path);
  return (
    <>
      <PageHead title={section?.label ?? ''} subtitle={section?.blurb ?? ''} />
      {children}
    </>
  );
}

export { Button };
